import {
  createRef, forwardRef, useContext, useLayoutEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import {
  DataGrid,
} from '@mui/x-data-grid';
import { Tooltip, TOOLTIP_VARIANTS } from '../tooltip/Tooltip.component';
import AppContext from '../../appLayout/App.context';

const StyledDataGrid = styled(DataGrid)`
  border: none;
  .MuiDataGrid-columnHeaders {
    border-radius: 0;
    line-height: 55px !important;
  }
  .MuiDataGrid-iconSeparator {
    display: none;
  }
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 600;
    color: ${({ theme }) => theme.palette.grey[100]};
  }
  .MuiDataGrid-columnHeader--sorted .MuiDataGrid-columnHeaderTitle {
    color: ${({ theme }) => theme.palette.primary.main};
  }
  .MuiDataGrid-columnHeader--sorted svg {
    fill: ${({ theme }) => theme.palette.primary.main};
  }
  .MuiDataGrid-cell {
    border-bottom-color: ${({ theme }) => theme.palette.grey[40]};
  }
  .MuiDataGrid-columnHeader:focus-within,
  .MuiDataGrid-cell:focus-within {
    outline: none;
  }
  .MuiDataGrid-row:hover {
    background: ${({ theme }) => theme.palette.primary[0]};
  }
  .MuiDataGrid-row.Mui-selected {
    background: ${({ theme }) => theme.palette.primary[5]};
  }
  .MuiIconButton-root:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.palette.common.white};
    outline: ${({ theme }) => theme.palette.primary.dark} solid 2px;
    border-radius: 6px;
    padding: 5px;
  }

  .MuiDataGrid-columnHeader:focus .MuiDataGrid-columnHeaderTitleContainer {
    outline: ${({ theme }) => theme.palette.primary.dark} solid 2px;
    margin: 2px;
  }
`;

/**
* Renders a <Table /> component with optional label.
* @param {Object} props
* @param {number | string} props.containerHeight - the height of the table, can be a number (pixels) or string (auto, vh, etc.).
* @param {string} props.dataTestIdPrefix - prefix value used for data-testid.
*/
export const Table = forwardRef(({
  containerHeight,
  dataTestIdPrefix,
  ...props
}, forwardedRef) => {
  const { t } = useContext(AppContext);
  // defaults: https://github.com/mui/mui-x/blob/HEAD/packages/grid/x-data-grid/src/constants/localeTextConstants.ts
  // some of the functions for handling plural strings will likely need to be updated
  const gridLocaleText = {
    noRowsLabel: t('noRowsLabel'),
    noResultsOverlayLabel: t('noResultsOverlayLabel'),
    errorOverlayDefaultLabel: t('errorOverlayDefaultLabel'),
    toolbarDensity: t('toolbarDensity'),
    toolbarDensityLabel: t('toolbarDensityLabel'),
    toolbarDensityCompact: t('toolbarDensityCompact'),
    toolbarDensityStandard: t('toolbarDensityStandard'),
    toolbarDensityComfortable: t('toolbarDensityComfortable'),
    toolbarColumns: t('toolbarColumns'),
    toolbarColumnsLabel: t('toolbarColumnsLabel'),
    toolbarFilters: t('toolbarFilters'),
    toolbarFiltersLabel: t('toolbarFiltersLabel'),
    toolbarFiltersTooltipHide: t('toolbarFiltersTooltipHide'),
    toolbarFiltersTooltipShow: t('toolbarFiltersTooltipShow'),
    toolbarFiltersTooltipActive: (count) => {
      const plural = count > 1 ? 's' : '';
      return t('toolbarFiltersTooltipActive', { count, plural });
    },
    toolbarExport: t('toolbarExport'),
    toolbarExportLabel: t('toolbarExportLabel'),
    toolbarExportCSV: t('toolbarExportCSV'),
    toolbarExportPrint: t('toolbarExportPrint'),
    columnsPanelTextFieldLabel: t('columnsPanelTextFieldLabel'),
    columnsPanelTextFieldPlaceholder: t('columnsPanelTextFieldPlaceholder'),
    columnsPanelDragIconLabel: t('columnsPanelDragIconLabel'),
    columnsPanelShowAllButton: t('columnsPanelShowAllButton'),
    columnsPanelHideAllButton: t('columnsPanelHideAllButton'),
    filterPanelAddFilter: t('filterPanelAddFilter'),
    filterPanelDeleteIconLabel: t('filterPanelDeleteIconLabel'),
    filterPanelLogicOperator: t('filterPanelLogicOperator'),
    filterPanelOperator: t('filterPanelOperator'),
    filterPanelOperatorAnd: t('filterPanelOperatorAnd'),
    filterPanelOperatorOr: t('filterPanelOperatorOr'),
    filterPanelColumns: t('filterPanelColumns'),
    filterPanelInputLabel: t('filterPanelInputLabel'),
    filterPanelInputPlaceholder: t('filterPanelInputPlaceholder'),
    filterOperatorContains: t('filterOperatorContains'),
    filterOperatorEquals: t('filterOperatorEquals'),
    filterOperatorStartsWith: t('filterOperatorStartsWith'),
    filterOperatorEndsWith: t('filterOperatorEndsWith'),
    filterOperatorIs: t('filterOperatorIs'),
    filterOperatorNot: t('filterOperatorNot'),
    filterOperatorAfter: t('filterOperatorAfter'),
    filterOperatorOnOrAfter: t('filterOperatorOnOrAfter'),
    filterOperatorBefore: t('filterOperatorBefore'),
    filterOperatorOnOrBefore: t('filterOperatorOnOrBefore'),
    filterOperatorIsEmpty: t('filterOperatorIsEmpty'),
    filterOperatorIsNotEmpty: t('filterOperatorIsNotEmpty'),
    filterOperatorIsAnyOf: t('filterOperatorIsAnyOf'),
    filterValueAny: t('filterValueAny'),
    filterValueTrue: t('filterValueTrue'),
    filterValueFalse: t('filterValueFalse'),
    columnMenuLabel: t('columnMenuLabel'),
    columnMenuShowColumns: t('columnMenuShowColumns'),
    columnMenuFilter: t('columnMenuFilter'),
    columnMenuHideColumn: t('columnMenuHideColumn'),
    columnMenuUnsort: t('columnMenuUnsort'),
    columnMenuSortAsc: t('columnMenuSortAsc'),
    columnMenuSortDesc: t('columnMenuSortDesc'),
    columnHeaderFiltersTooltipActive: (count) => {
      const plural = count > 1 ? 's' : '';
      return t('columnHeaderFiltersTooltipActive', { count, plural });
    },
    columnHeaderFiltersLabel: t('columnHeaderFiltersLabel'),
    columnHeaderSortIconLabel: t('columnHeaderSortIconLabel'),
    footerRowSelected: (count) => {
      const plural = count > 1 ? 's' : '';
      return t('footerRowSelected', { count, plural });
    },
    footerTotalRows: t('footerTotalRows'),
    footerTotalVisibleRows: (visibleCount, totalCount) => t('footerTotalVisibleRows', { visibleCount, totalCount }),
    checkboxSelectionHeaderName: t('checkboxSelectionHeaderName'),
    checkboxSelectionSelectAllRows: t('checkboxSelectionSelectAllRows'),
    checkboxSelectionUnselectAllRows: t('checkboxSelectionUnselectAllRows'),
    checkboxSelectionSelectRow: t('checkboxSelectionSelectRow'),
    checkboxSelectionUnselectRow: t('checkboxSelectionUnselectRow'),
    booleanCellTrueLabel: t('booleanCellTrueLabel'),
    booleanCellFalseLabel: t('booleanCellFalseLabel'),
    actionsCellMore: t('actionsCellMore'),
    pinToLeft: t('pinToLeft'),
    pinToRight: t('pinToRight'),
    unpin: t('unpin'),
    treeDataGroupingHeaderName: t('treeDataGroupingHeaderName'),
    treeDataExpand: t('treeDataExpand'),
    treeDataCollapse: t('treeDataCollapse'),
    groupingColumnHeaderName: t('groupingColumnHeaderName'),
    groupColumn: (name) => t('groupColumn', { name }),
    unGroupColumn: (name) => t('unGroupColumn', { name }),
    expandDetailPanel: t('expandDetailPanel'),
    collapseDetailPanel: t('collapseDetailPanel'),
  };

  return (
    <div style={{ height: containerHeight }}>
      <StyledDataGrid
        data-testid={`${dataTestIdPrefix}_Table`}
        disableColumnFilter
        disableColumnMenu
        disableDensitySelector
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
        slotProps={{
          pagination: {
            labelRowsPerPage: t('footerRowsPerPage'),
            getItemAriaLabel: (type) => (type === 'next' ? t('goToNextPage') : t('goToPreviousPage')),
          },
        }}
        localeText={gridLocaleText}
        ref={forwardedRef}
        {...props}
      />
    </div>
  );
});

Table.propTypes = {
  containerHeight: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  dataTestIdPrefix: PropTypes.string,
};

Table.defaultProps = {
  containerHeight: '100%',
  dataTestIdPrefix: 'Table',
};

/**
 * Renders a <TruncatedCellWithTooltip /> component.
 * @param {object} props.children This is an array of React components.
 * @param {function} props.text
 * @returns {React.Component}
 */
export const TruncatedCellWithTooltip = ({
  children,
  text,
  ...other
}) => {
  const ref = createRef();
  const [showTooltip, setShowTooltip] = useState(false);

  useLayoutEffect(() => {
    if (ref.current.clientWidth < ref.current.scrollWidth) {
      setShowTooltip(true);
    }
  }, [ref]);

  return showTooltip ? (
    <Tooltip title={text} variant={TOOLTIP_VARIANTS.DARK} placement="top-start" arrow>
      <div ref={ref} className="MuiDataGrid-cellContent" {...other}>
        { children }
      </div>
    </Tooltip>
  ) : (
    <div ref={ref} className="MuiDataGrid-cellContent" {...other}>
      { children }
    </div>
  );
};

TruncatedCellWithTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  text: PropTypes.string.isRequired,
};
